import React from 'react';
import { CheckCircle } from 'lucide-react';
import { siteContent } from '../data/mockData';
import { useScrollAnimation, useStaggeredAnimation } from '../hooks/useScrollAnimation';

const HowWeWork = ({ onNavigate }) => {
  const content = siteContent.en?.howWeWork || siteContent.en.howWeWork;
  const [headerRef, headerVisible] = useScrollAnimation();
  const [stepsRef, visibleSteps] = useStaggeredAnimation(4, 200);
  const [ctaRef, ctaVisible] = useScrollAnimation(400);

  return (
    <section id="how-we-work" className="py-20 lg:py-32 bg-gradient-to-r from-[#f0f0e8] to-white">
      <div className="container mx-auto px-4 lg:px-6">
        {/* Section Header */}
        <div ref={headerRef} className={`text-center mb-16 animate-fade-in-up ${headerVisible ? 'visible' : ''}`}>
          <div className="inline-flex items-center bg-white rounded-full px-4 py-2 mb-6 shadow-sm">
            <CheckCircle className="w-4 h-4 text-[#c41927] mr-2" />
            <span className="text-sm font-semibold text-[#011431] uppercase tracking-wide">Our Process</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-[#011431] mb-6">
            {content.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {content.subtitle}
          </p>
        </div>
        
        {/* Process Steps */}
        <div ref={stepsRef} className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {content.steps.map((step, index) => (
            <div key={index} className={`text-center group animate-fade-in-up ${visibleSteps.includes(index) ? 'visible' : ''}`}>
              <div className="relative mb-6">
                {/* Step Number */}
                <div className="w-20 h-20 bg-gradient-to-br from-[#c41927] to-[#a01520] text-white rounded-full flex items-center justify-center font-bold text-xl mb-4 mx-auto group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  {step.step}
                </div>
                
                {/* Connector Line (hidden on last item) */}
                {index < content.steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-[#c41927] to-[#f0f0e8] transform -translate-y-0.5 z-0"></div>
                )}
              </div>
              
              <h3 className="text-xl font-bold text-[#011431] mb-3 group-hover:text-[#c41927] transition-colors">
                {step.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {step.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div ref={ctaRef} className={`text-center mt-16 pt-8 border-t border-gray-200 animate-fade-in-up ${ctaVisible ? 'visible' : ''}`}>
          <p className="text-lg text-gray-600 mb-6">
            Ready to start your compliance journey with a proven methodology?
          </p>
          <button
            onClick={() => {
              if (onNavigate) {
                onNavigate('contact');
              }
            }}
            className="bg-[#c41927] hover:bg-[#a01520] text-white font-semibold px-8 py-3 rounded-lg transition-all duration-300 transform hover:scale-105"
          >
            Start Your Assessment
          </button>
        </div>
      </div>
    </section>
  );
};

export default HowWeWork;